import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    UserExternalRewardsFilter,
    UserLendingVaultFilter,
    UserVaultExternalRewardsPnlResponse,
    UserPositionDepositSummaryResponse,
    UserPositionDepositSummaryParams,
    UpdateVaultMetadataParams
} from "../types";
import { ApiVaultDepositResponse } from "../types/api";
import { VaultDepositResponse } from "../types/domain";
import { parseVaultDepositResponseFromApi } from "../types/parsers";
import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";
import {
    ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
    ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
    ROUTE_GET_USER_POSITION_DEPOSIT_SUMMARY,
    ROUTE_GET_VAULT_ACCESS,
    ROUTE_UPDATE_VAULT_METADATA
} from "../constants";

const LENDING_VAULT_POSITION_TAG = "LendingVault";
const LENDING_VAULT_METADATA_TAG = "LendingVaultMetadata";
const LENDING_VAULT_ACCESS_TAG = "LendingVaultAccess";

/** Response for `GET /lending_vaults/access` — whether the connected wallet may access the vault. */
export interface VaultAccessResponse {
    allowed: boolean;
}

export const lendingVaultApi = createApi({
    reducerPath: "abfLendingVaultApi",
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    tagTypes: [LENDING_VAULT_POSITION_TAG, LENDING_VAULT_METADATA_TAG, LENDING_VAULT_ACCESS_TAG],
    endpoints: (builder) => ({
        lendingVaultPositions: builder.query<VaultDepositResponse, UserLendingVaultFilter>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (response: ApiVaultDepositResponse) => parseVaultDepositResponseFromApi(response)
        }),
        userPositionDepositSummary: builder.query<UserPositionDepositSummaryResponse, UserPositionDepositSummaryParams>(
            {
                providesTags: [LENDING_VAULT_POSITION_TAG],
                query(params) {
                    return {
                        url: ROUTE_GET_USER_POSITION_DEPOSIT_SUMMARY,
                        method: "POST",
                        body: params
                    };
                }
            }
        ),
        userVaultExternalRewardsPnl: builder.query<
            UserVaultExternalRewardsPnlResponse | null | undefined,
            UserExternalRewardsFilter
        >({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (
                response: UserVaultExternalRewardsPnlResponse | null | undefined
            ): UserVaultExternalRewardsPnlResponse | null | undefined => {
                if (response === null) return null;
                if (response === undefined) return undefined;
                if (Object.keys(response).length === 0) return null;
                return response;
            }
        }),
        vaultAccess: builder.query<VaultAccessResponse, { vaultAddress: string }>({
            providesTags: [LENDING_VAULT_ACCESS_TAG],
            query({ vaultAddress }) {
                return {
                    url: ROUTE_GET_VAULT_ACCESS,
                    method: "GET",
                    params: { vaultAddress }
                };
            }
        }),
        updateVaultMetadataWithSignature: builder.mutation<void, UpdateVaultMetadataParams>({
            invalidatesTags: [LENDING_VAULT_METADATA_TAG],
            query(params) {
                return {
                    url: ROUTE_UPDATE_VAULT_METADATA,
                    method: "POST",
                    body: params
                };
            }
        })
    })
});

export const {
    useLendingVaultPositionsQuery,
    useUserPositionDepositSummaryQuery,
    useUserVaultExternalRewardsPnlQuery,
    useVaultAccessQuery,
    useUpdateVaultMetadataWithSignatureMutation
} = lendingVaultApi;

export function useLendingVaultApi() {
    const dispatch = useDispatch();

    return {
        resetLendingVaultPositionsApi: () =>
            dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_POSITION_TAG])),
        resetLendingVaultMetadataApi: () => dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_METADATA_TAG])),
        resetLendingVaultAccessApi: () => dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_ACCESS_TAG]))
    };
}
